/** Max chars for any nested "stack" string before truncation (kubelet line limits). */
const maxStructuredLogStackChars = 4096;
/** If the JSON line still exceeds this, emit a minimal fallback record instead. */
const maxStructuredLogLineChars = 12000;
const callHandlers = (handlers, event) => {
    for (const handler of handlers) {
        try {
            handler(event);
        }
        catch (error) {
            if (typeof console !== "undefined" && typeof console.error === "function") {
                const fallback = JSON.stringify({
                    ts: new Date().toISOString(),
                    level: "error",
                    msg: "structured_log_handler_failure",
                    error: error instanceof Error ? error.message : String(error),
                });
                console.error(fallback);
            }
        }
    }
};
/**
 * Truncates long "stack" string fields in-place on nested plain objects (shallow walk).
 * Keeps log lines under kubelet / Loki JSON parser limits.
 */
const truncateStackFieldsDeep = (value) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return;
    }
    const obj = value;
    for (const key of Object.keys(obj)) {
        const v = obj[key];
        if (key === "stack" && typeof v === "string" && v.length > maxStructuredLogStackChars) {
            obj[key] = `${v.slice(0, maxStructuredLogStackChars)}...[truncated]`;
        }
        else if (v !== null && typeof v === "object" && !Array.isArray(v)) {
            truncateStackFieldsDeep(v);
        }
    }
};
export const createStructuredLogger = (...handlers) => {
    const activeHandlers = handlers.filter(Boolean);
    const emit = (event) => {
        if (activeHandlers.length === 0) {
            return;
        }
        callHandlers(activeHandlers, event);
    };
    return {
        emit,
        debug: (event, metadata) => emit({ level: "debug", event, metadata }),
        info: (event, metadata) => emit({ level: "info", event, metadata }),
        warn: (event, metadata) => emit({ level: "warn", event, metadata }),
        error: (event, metadata) => emit({ level: "error", event, metadata }),
    };
};
export const createConsoleLogHandler = (options) => {
    return event => {
        if (typeof console === "undefined") {
            return;
        }
        // Single-line JSON: Node's console.error(msg, obj) uses util.inspect (not JSON),
        // which breaks Loki's | json stage and can split/truncate across kubelet line limits.
        const record = {
            ...(event.metadata ? { ...event.metadata } : {}),
            ts: new Date().toISOString(),
            level: event.level,
            namespace: options?.namespace ?? "api-client",
            msg: event.event,
        };
        truncateStackFieldsDeep(record);
        let line;
        try {
            line = JSON.stringify(record);
        }
        catch {
            line = JSON.stringify({
                ts: new Date().toISOString(),
                level: event.level,
                namespace: options?.namespace ?? "api-client",
                msg: event.event,
                metadata_error: "JSON.stringify failed for structured log record",
            });
        }
        if (line.length > maxStructuredLogLineChars) {
            line = JSON.stringify({
                ts: new Date().toISOString(),
                level: event.level,
                namespace: options?.namespace ?? "api-client",
                msg: event.event,
                truncated: true,
                original_length: line.length,
            });
        }
        switch (event.level) {
            case "error":
                console.error(line);
                break;
            case "warn":
                console.warn(line);
                break;
            case "debug":
                console.debug(line);
                break;
            default:
                console.info(line);
                break;
        }
    };
};
const serialiseUnknown = (value) => {
    if (typeof value === "string") {
        return value;
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
};
export const normalizeErrorMetadata = (error) => {
    if (error instanceof Error) {
        const cause = error.cause;
        const serializedCause = cause ? serialiseUnknown(cause) : undefined;
        const digest = "digest" in error && typeof error.digest === "string"
            ? error.digest
            : undefined;
        return {
            type: "Error",
            name: error.name,
            message: error.message,
            stack: error.stack ?? undefined,
            ...(serializedCause ? { cause: serializedCause } : {}),
            ...(digest ? { digest } : {}),
        };
    }
    if (typeof error === "string") {
        return {
            type: "string",
            message: error,
        };
    }
    if (typeof error === "object" && error !== null) {
        const maybeMessage = error.message;
        const maybeStack = error.stack;
        const maybeName = error.name;
        if (typeof maybeMessage === "string") {
            return {
                type: typeof maybeName === "string" ? maybeName : "object",
                name: typeof maybeName === "string" ? maybeName : undefined,
                message: maybeMessage,
                stack: typeof maybeStack === "string" ? maybeStack : undefined,
            };
        }
        return {
            type: "object",
            message: serialiseUnknown(error),
        };
    }
    return {
        type: typeof error,
        message: String(error),
    };
};
export const ensureError = (error) => {
    if (error instanceof Error) {
        return error;
    }
    if (typeof error === "string") {
        return new Error(error);
    }
    try {
        return new Error(serialiseUnknown(error));
    }
    catch {
        return new Error("Unknown error");
    }
};
